import React, { useState, useEffect } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Info } from 'lucide-react';
import { fetchLinkedConnectors } from '@/utils/connectorApi';
import { StandardizedConnection } from '@/types/connections';
import { formatFivetranSyncDetail, isRescheduledSyncState } from '@/utils/fivetranSyncStatus';

interface SyncInProgressBannerProps {
  organisation: { connections?: unknown };
  /** When set and non-empty, only these connector IDs are considered (matches `connectorId` / `id`). */
  selectedConnectorIds?: string[];
}

function connectionRowsFromOrg(organisation: { connections?: unknown }): Record<string, unknown>[] {
  const connections = organisation?.connections;
  if (!connections) return [];
  if (Array.isArray(connections)) return connections as Record<string, unknown>[];
  if (typeof connections === 'object') {
    return Object.values(connections as Record<string, unknown>).filter(
      (v): v is Record<string, unknown> => v !== null && typeof v === 'object'
    );
  }
  return [];
}

function disambiguateNames<T extends { name: string; connectorId: string }>(items: T[]): T[] {
  const nameCounts: Record<string, number> = {};
  items.forEach((s) => {
    nameCounts[s.name] = (nameCounts[s.name] || 0) + 1;
  });

  return items.map((s) => {
    if (nameCounts[s.name] <= 1) return s;
    const servicePrefix = s.name?.toLowerCase() || '';
    const suffix = (s.connectorId || '')
      .replace(new RegExp(`^${servicePrefix}_`, 'i'), '')
      .replace(/_/g, ' ')
      .trim();
    if (suffix && suffix !== s.connectorId) {
      return { ...s, name: `${s.name} (${suffix})` };
    }
    const dupes = items.filter((d) => d.name === s.name);
    const idx = dupes.indexOf(s) + 1;
    return { ...s, name: `${s.name} (${idx})` };
  });
}

const SyncInProgressBanner: React.FC<SyncInProgressBannerProps> = ({
  organisation,
  selectedConnectorIds,
}) => {
  const [syncingConnections, setSyncingConnections] = useState<{ name: string; connectorId: string }[]>([]);
  const [rescheduledConnections, setRescheduledConnections] = useState<
    { name: string; connectorId: string; detail: string | null }[]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFreshStatus = async () => {
      try {
        const connList = connectionRowsFromOrg(organisation);

        const connectorIds = connList
          .filter((conn) => {
            const connectorId = String(conn.connectorId ?? conn.id ?? conn.connectionId ?? '');
            const service = String(conn.service ?? '').toLowerCase();
            const type = String(conn.type ?? '').toLowerCase();
            const isCsv = connectorId.startsWith('csv-import-') || service === 'csv' || type === 'csv';
            const isPlaid = service === 'plaid' || type === 'plaid';
            const isFileImport = service === 'file' || type === 'file' || connectorId.startsWith('file-import-');
            return !isCsv && !isPlaid && !isFileImport;
          })
          .map((conn) => String(conn.connectorId ?? conn.id ?? conn.connectionId ?? ''))
          .filter(Boolean);

        if (connectorIds.length === 0) {
          setSyncingConnections([]);
          setRescheduledConnections([]);
          setLoading(false);
          return;
        }

        const freshConnectors = await fetchLinkedConnectors(connectorIds);

        const selectedSet = selectedConnectorIds?.length
          ? new Set(selectedConnectorIds)
          : null;

        const matchesSelection = (conn: StandardizedConnection) => {
          if (!selectedSet) return true;
          const cid = conn.connectorId || conn.id || '';
          return selectedSet.has(cid);
        };

        const syncingRaw = freshConnectors
          .filter((conn: StandardizedConnection) => {
            if (!matchesSelection(conn)) return false;
            if (conn.setup_state && conn.setup_state !== 'connected') return false;
            if (conn.status === 'error') return false;
            if (conn.metadata?.status?.setup_state && conn.metadata.status.setup_state !== 'connected') return false;
            if (conn.metadata?.status?.sync_state === 'paused') return false;

            return conn.metadata?.status?.is_historical_sync === true;
          })
          .map((conn: StandardizedConnection) => ({
            name: conn.name || 'Unknown',
            connectorId: conn.connectorId || conn.id,
          }));

        const rescheduledRaw = freshConnectors
          .filter((conn: StandardizedConnection) => {
            if (!matchesSelection(conn)) return false;
            if (conn.setup_state && conn.setup_state !== 'connected') return false;
            if (conn.status === 'error') return false;
            if (conn.metadata?.status?.setup_state && conn.metadata.status.setup_state !== 'connected') return false;
            if (conn.metadata?.status?.sync_state === 'paused') return false;

            return isRescheduledSyncState(conn.metadata?.status?.sync_state as string | undefined);
          })
          .map((conn: StandardizedConnection) => {
            const st = conn.metadata?.status as Record<string, unknown> | undefined;
            return {
              name: conn.name || 'Unknown',
              connectorId: conn.connectorId || conn.id,
              detail: formatFivetranSyncDetail({
                syncState: st?.sync_state as string | undefined,
                rescheduledFor: (st?.rescheduled_for as string | null | undefined) ?? null,
                tasks: st?.tasks as Array<{ message?: string; details?: string }> | undefined,
                warnings: st?.warnings as Array<{ message?: string; details?: string }> | undefined,
                statusReason: st?.reason,
              }),
            };
          });

        setSyncingConnections(disambiguateNames(syncingRaw));
        setRescheduledConnections(disambiguateNames(rescheduledRaw));
      } catch (error) {
        console.error('Error fetching fresh connector status:', error);
        setSyncingConnections([]);
        setRescheduledConnections([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFreshStatus();
  }, [organisation, selectedConnectorIds]);

  if (loading || (syncingConnections.length === 0 && rescheduledConnections.length === 0)) return null;

  return (
    <div className="space-y-3 mb-4">
      {syncingConnections.length > 0 ? (
        <Alert variant="default" className="mb-0">
          <Info className="h-4 w-4" />
          <AlertTitle>Historical data sync in progress</AlertTitle>
          <AlertDescription>
            <p className="mt-1">
              {syncingConnections.length === 1
                ? `${syncingConnections[0].name} is still syncing historical data. Report data may be incomplete until the sync finishes.`
                : `${syncingConnections.map((c) => c.name).join(', ')} are still syncing historical data. Report data may be incomplete until syncs finish.`}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {syncingConnections.map((conn) => (
                <a
                  key={conn.connectorId}
                  href={`https://fivetran.com/dashboard/connectors/${conn.connectorId}/status`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium underline hover:no-underline"
                >
                  View Sync Status{syncingConnections.length > 1 ? ` (${conn.name})` : ''}
                </a>
              ))}
            </div>
          </AlertDescription>
        </Alert>
      ) : null}

      {rescheduledConnections.length > 0 ? (
        <Alert variant="default" className="mb-0 border-border bg-muted/40">
          <Info className="h-4 w-4" />
          <AlertTitle>Sync rescheduled</AlertTitle>
          <AlertDescription>
            <p className="mt-1 text-sm text-muted-foreground">
              {rescheduledConnections.length === 1
                ? `${rescheduledConnections[0].name} has an active sync that Fivetran rescheduled (often due to source API limits). Partial data may already be available.`
                : `${rescheduledConnections.map((c) => c.name).join(', ')} have syncs that were rescheduled. Partial data may already be available.`}
            </p>
            <ul className="mt-2 list-disc list-inside text-sm space-y-1">
              {rescheduledConnections.map((c) => (
                <li key={c.connectorId}>
                  <span className="font-medium text-foreground">{c.name}</span>
                  {c.detail ? `: ${c.detail}` : null}
                </li>
              ))}
            </ul>
            <div className="mt-2 flex flex-wrap gap-2">
              {rescheduledConnections.map((conn) => (
                <a
                  key={`rs-${conn.connectorId}`}
                  href={`https://fivetran.com/dashboard/connectors/${conn.connectorId}/status`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium underline hover:no-underline"
                >
                  View status{rescheduledConnections.length > 1 ? ` (${conn.name})` : ''}
                </a>
              ))}
            </div>
          </AlertDescription>
        </Alert>
      ) : null}
    </div>
  );
};

export default SyncInProgressBanner;
